import { WeaponChargeView } from "game-common/charged_weapon"
import { BLEND_MODES, Graphics } from "pixi.js"

/**
 * The Ember Core drawing breath. One hold, three readable states: gathering, armed, and the
 * moment between them.
 *
 * This used to be a 15px amber arc over the character's hip, which told the player the truth and
 * nothing else -- a progress bar wearing a costume. The charge is 800ms of standing still at half
 * speed in front of something that wants to kill you, so it has to be worth watching.
 *
 * TWO LAYERS, ON OPPOSITE SIDES OF THE BODY. Everything here used to be one Graphics added after the
 * sprite, which meant the ground art painted straight over the character's legs and the ring looked
 * like it was stuck ON them rather than drawn AROUND them. So:
 *
 *   - `ground` is inserted BELOW the sprite (see LpcComposite): a scorch pool and the dial itself,
 *     lying flat on the floor with the character standing inside them. Same trick as the player ring
 *     (player_ring.ts), and the same reason -- a mark on the floor has to be occluded by the body
 *     standing on it or it reads as a decal pasted on the screen.
 *   - `this` is added ABOVE the sprite: the molten core gathering at the chest, the motes rising out
 *     of the pool and the sparks spiralling in. These are in front of the body, so they belong there,
 *     and the whole layer is ADDITIVE because every one of them is emitted light. Drawn normally,
 *     the core came out as a flat orange disc with a hard edge -- a wax seal pinned to the chest.
 *     Additive is also why its colours come from the `blaze` end of the palette: see FORGE.
 *
 * ARMED IS A DIFFERENT PICTURE, NOT A BRIGHTER ONE. At progress 1 the dial stops being an arc and
 * closes into a ring, the inbound sparks lock into a fixed triangle, and a flash fires once. "Can I
 * release yet" must be answerable out of the corner of an eye, mid-fight, on a small screen -- a hue
 * shift alone does not survive that.
 *
 * Server durations are relative: remainingMs is re-anchored to local time every message (the server
 * rebuilds the view object each tick), and interpolated down between them. No clock sync required.
 */

// The forge palette. Shared with AreaProjectile on purpose -- the charge, the shot and the blast are
// one object seen at three moments, and they are only read that way if they are the same colour.
export const FORGE = {
    scorch: 0x140a05,
    coal: 0x2b1408,
    rust: 0x8d3a12,
    ember: 0xff6d22,
    flame: 0xffa13b,
    gold: 0xffd287,
    hot: 0xfff2d2,
    white: 0xffffff,
    // For ADDITIVE layers only. `ember` has enough green in it that adding it to grass lands on
    // lime -- an Ember Core blast over the camp lawn came out looking radioactive. Additive blending
    // adds channels, so the hot colour has to be starved of everything the ground already has.
    blaze: 0xff3d08,
}

// Where the art sits in the composite's local space, which is WORLD PIXELS: the LPC frame is 64x64
// placed at (-32,-32) with no scale, so one unit here is one world pixel and (0,0) is the entity's
// own location.
//
// EVERY NUMBER BELOW WAS MEASURED OFF THE RUNNING GAME. Two earlier passes reasoned about it instead
// and both landed the core on the character's face: the first borrowed the Bonecaller's channel
// offsets, which live in the OVERLAY surface's space and are roughly twice this scale, and the
// second assumed the origin sat at the hip. On a standing LPC humanoid in THIS space the soles land
// near y=+26 and the hair line near y=-16, which puts the origin around the shoulders. The other
// anchor worth knowing is the player ring (player_ring.ts): a player's ground footprint is 27x12.5.
const FOOT_Y = 26
// Chest height -- below the origin, not above it.
const CORE_Y = 4
// The ground dial, sized just outside the player ring's footprint so it reads as this character's
// own patch of ground. Squash is what makes a circle lie down in this game's projection.
const DIAL_R = 13
const SQUASH = 0.46
// How long the lock-in flash lasts. Short: it is a punctuation mark, not a state.
const LOCK_FLASH_MS = 220
const FADE_OUT_MS = 250
// Sparks pulled in out of the air, and motes lifted off the ground.
const SPARKS = 3
const MOTES = 4

export class WeaponCharge extends Graphics {
    /**
     * The floor half of the effect. The owner must put this UNDER the body -- LpcComposite inserts
     * it at child index 0 -- and destroy it alongside this object; it is a sibling, not a child, so
     * that the sprite can sort between the two.
     */
    readonly ground = new Graphics()

    constructor() {
        super()
        this.blendMode = BLEND_MODES.ADD
    }

    private view?: WeaponChargeView
    private receivedAt = 0
    // Keyed on the charge id, not the view object: the server mints a fresh view every tick, so
    // object identity changes constantly while the hold itself does not.
    private lockedId?: string
    private lockedAt = 0
    private fadeStartedAt?: number

    draw = (view: WeaponChargeView | undefined, now = Date.now()) => {
        if (view !== this.view) {
            if (!view && this.view) this.fadeStartedAt = now
            this.view = view
            this.receivedAt = now
        }
        if (!view) {
            this.lockedId = undefined
            // Keep the last charge frame intact as both sibling layers fade together.
            const remaining = this.fadeStartedAt === undefined ? 0 :
                Math.max(0, Math.min(1, 1 - (now - this.fadeStartedAt) / FADE_OUT_MS))
            this.alpha = this.ground.alpha = remaining * remaining
            this.visible = this.ground.visible = remaining > 0
            if (!remaining) {
                this.clear()
                this.ground.clear()
                this.fadeStartedAt = undefined
            }
            return
        }
        this.fadeStartedAt = undefined
        this.alpha = this.ground.alpha = 1
        this.visible = this.ground.visible = true
        this.clear()
        this.ground.clear()
        const progress = Math.max(0, Math.min(1, 1 - (view.remainingMs - now + this.receivedAt) / view.durationMs))
        const armed = progress >= 1
        if (!armed || this.lockedId !== view.id) {
            if (armed) {
                this.lockedId = view.id
                this.lockedAt = now
            } else if (this.lockedId === view.id) {
                // A fresh message can walk progress back a frame; it must not re-fire the flash.
                this.lockedId = undefined
            }
        }
        const flash = armed && this.lockedId === view.id
            ? Math.max(0, 1 - (now - this.lockedAt) / LOCK_FLASH_MS) : 0

        this.drawPool(progress, armed, now)
        this.drawDial(progress, armed, flash, now)
        this.drawMotes(progress, now)
        this.drawCore(progress, armed, flash, now)
        this.drawSparks(progress, armed, now)
    }

    destroy(options?: Parameters<Graphics["destroy"]>[0]) {
        this.ground.destroy(options)
        super.destroy(options)
    }

    /** Scorched ground under the caster: a dark bruise with heat bleeding out of it. */
    private drawPool = (progress: number, armed: boolean, now: number) => {
        const g = this.ground
        const r = DIAL_R * (0.55 + 0.45 * progress)
        g.lineStyle(0)
        g.beginFill(FORGE.scorch, 0.06 + 0.13 * progress)
        g.drawEllipse(0, FOOT_Y, r * 1.5, r * 1.5 * SQUASH)
        g.endFill()
        g.beginFill(FORGE.ember, 0.1 + 0.22 * progress + (armed ? 0.06 * Math.sin(now / 110) : 0))
        g.drawEllipse(0, FOOT_Y, r, r * SQUASH)
        g.endFill()
        g.beginFill(FORGE.flame, 0.08 + 0.2 * progress)
        g.drawEllipse(0, FOOT_Y, r * 0.5, r * 0.5 * SQUASH)
        g.endFill()
    }

    /**
     * The dial, lying flat on the ground. A cold iron backing ring, the heated arc burning around
     * it from due north, a travelling head spark, and tick marks that light as the arc passes.
     *
     * Armed closes the circle: the geometry changes, not just the colour.
     */
    private drawDial = (progress: number, armed: boolean, flash: number, now: number) => {
        const g = this.ground
        const start = -Math.PI / 2
        g.lineStyle(1.4, FORGE.coal, 0.6)
        this.groundArc(DIAL_R, start, start + Math.PI * 2)
        if (armed) {
            g.lineStyle(3.5, FORGE.flame, 0.25 + 0.45 * flash)
            this.groundArc(DIAL_R, start, start + Math.PI * 2)
            g.lineStyle(1.5, FORGE.gold, 0.9)
            this.groundArc(DIAL_R, start, start + Math.PI * 2)
        } else {
            const end = start + Math.PI * 2 * progress
            // A wide dim pass under a narrow bright one: a fake bloom, since filters are off the
            // table here (one Graphics per charging entity, on budget hardware).
            g.lineStyle(4, FORGE.rust, 0.3)
            this.groundArc(DIAL_R, start, end)
            g.lineStyle(1.8, FORGE.flame, 0.85)
            this.groundArc(DIAL_R, start, end)
            g.lineStyle(0)
            g.beginFill(FORGE.gold, 0.95)
            g.drawCircle(Math.cos(end) * DIAL_R, FOOT_Y + Math.sin(end) * DIAL_R * SQUASH, 1.6)
            g.endFill()
        }
        for (let i = 0; i < 8; i++) {
            const a = start + i * Math.PI / 4
            const lit = armed || a <= start + Math.PI * 2 * progress
            g.lineStyle(1.2, lit ? FORGE.gold : FORGE.coal, lit ? 0.8 : 0.4)
            g.moveTo(Math.cos(a) * (DIAL_R - 2), FOOT_Y + Math.sin(a) * (DIAL_R - 2) * SQUASH)
            g.lineTo(Math.cos(a) * (DIAL_R + 2), FOOT_Y + Math.sin(a) * (DIAL_R + 2) * SQUASH)
        }
        if (flash > 0) {
            g.lineStyle(2 * flash, FORGE.hot, flash * 0.7)
            this.groundArc(DIAL_R + 7 * (1 - flash), start, start + Math.PI * 2)
        }
    }

    /** Embers lifted off the pool and drawn up toward the core: where the power is coming FROM. */
    private drawMotes = (progress: number, now: number) => {
        this.lineStyle(0)
        for (let i = 0; i < MOTES; i++) {
            const rise = ((now / (560 - 220 * progress) + i / MOTES) % 1)
            const x = Math.sin(now / 340 + i * 2.3) * DIAL_R * 0.8 * (1 - rise)
            const y = FOOT_Y - rise * (FOOT_Y - CORE_Y)
            this.beginFill(rise > 0.6 ? FORGE.gold : FORGE.blaze, (0.3 + 0.5 * progress) * (1 - rise * 0.4))
            this.drawCircle(x, y, 0.7 + 1.0 * (1 - rise))
            this.endFill()
        }
    }

    /** The charge itself, gathering at the chest. Three shells so it has an edge and a hot centre. */
    private drawCore = (progress: number, armed: boolean, flash: number, now: number) => {
        const breath = armed ? 1 + 0.1 * Math.sin(now / 95) : 1
        const r = (0.8 + 2.6 * progress) * breath
        this.lineStyle(0)
        // Four shells, each smaller and hotter. The white is kept to the innermost one: a wide pale
        // fill is what turned the first pass into a featureless cream disc with no centre to it.
        this.beginFill(FORGE.blaze, (0.1 + 0.2 * progress) * breath)
        this.drawCircle(0, CORE_Y, r * 2.6)
        this.endFill()
        this.beginFill(FORGE.blaze, 0.35 + 0.4 * progress)
        this.drawCircle(0, CORE_Y, r * 1.7)
        this.endFill()
        this.beginFill(FORGE.flame, 0.55 + 0.3 * progress)
        this.drawCircle(0, CORE_Y, r * 1.05)
        this.endFill()
        this.beginFill(armed ? FORGE.hot : FORGE.gold, 0.9)
        this.drawCircle(0, CORE_Y, r * 0.5)
        this.endFill()
        if (flash > 0) {
            this.beginFill(FORGE.gold, flash * 0.5)
            this.drawCircle(0, CORE_Y, r + 11 * (1 - flash))
            this.endFill()
        }
    }

    /**
     * Sparks spiralling in. Their radius collapses with progress, so the hold has a visible target:
     * the shot is ready when they arrive. Armed stops the spiral dead and holds them in a triangle
     * -- motion stopping is a louder signal than motion speeding up.
     */
    private drawSparks = (progress: number, armed: boolean, now: number) => {
        const r = armed ? 10 : 20 - 10 * progress
        for (let i = 0; i < SPARKS; i++) {
            const a = (armed ? 0 : now / 260) + i * (Math.PI * 2 / SPARKS)
            const x = Math.cos(a) * r, y = CORE_Y + Math.sin(a) * r * 0.75
            // A head and a tail, both thin: fat marks on an additive layer stop being sparks and
            // start being white pills stuck to the character.
            this.lineStyle(0.9, FORGE.flame, armed ? 0.8 : 0.3 + 0.45 * progress)
            this.moveTo(x, y)
            this.lineTo(x - Math.cos(a) * 5 - Math.sin(a) * 3, y - (Math.sin(a) * 5 - Math.cos(a) * 3) * 0.75)
            this.lineStyle(0)
            this.beginFill(armed ? FORGE.hot : FORGE.gold, armed ? 0.95 : 0.5 + 0.45 * progress)
            this.drawCircle(x, y, armed ? 0.9 : 0.6 + 0.5 * progress)
            this.endFill()
        }
    }

    /** An arc flattened into the ground plane. Pixi's own arc() is a circle; this one lies down. */
    private groundArc = (r: number, a0: number, a1: number) => {
        const span = a1 - a0
        if (Math.abs(span) < 1e-4) return
        const steps = Math.max(2, Math.ceil(Math.abs(span) / 0.2))
        for (let i = 0; i <= steps; i++) {
            const a = a0 + span * i / steps
            const x = Math.cos(a) * r, y = FOOT_Y + Math.sin(a) * r * SQUASH
            if (i === 0) this.ground.moveTo(x, y)
            else this.ground.lineTo(x, y)
        }
    }
}
